import { Badge } from "@admin/components/atoms/Badge";
import { Button } from "@admin/components/atoms/Button";
import { Info } from "@admin/components/molecules/Person/Info";
import { DataTable, DataTableColumn } from "@admin/components/molecules/Table";
import { useCompany } from "@admin/hooks/useCompany";
import {
  getAllRoles,
  getAllWageTypes,
  getCompanyLocationsAndUsers,
  Personnel,
} from "@enos-monorepo/lib";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ActiveActions, InactiveActions } from "./Actions";
import EditPasswordModal from "./EditPasswordModal";
import ActivatePersonnelModal from "./personnelModal/ActivatePersonnelModal";
import { AddPersonnelModal } from "./personnelModal/AddPersonnelModal";
import DeactivatePersonnelModal from "./personnelModal/DeactivePersonnelModal";
import EditPersonnelModal from "./personnelModal/EditPersonnelModal";

export function PersonnelTables() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { company } = useCompany();

  const [selectedPersonnel, setSelectedPersonnel] = useState<Personnel>();
  const [deactivatePersonnelModalShow, setDeactivatePersonnelModalShow] =
    useState(false);
  const [activatePersonnelModalShow, setActivatePersonnelModalShow] =
    useState(false);
  const [isOpenEditPersonnelModal, setIsOpenEditPersonnelModal] =
    useState(false);
  const [isOpenAddPersonnelModal, setIsOpenAddPersonnelModal] = useState(false);
  const [isOpenPasswordEditModal, setIsOpenPasswordEditModal] = useState(false);

  const { data: users, isPending } = useQuery({
    queryKey: ["allLocationUsers", company?.companyId],
    queryFn: () => getCompanyLocationsAndUsers(company?.companyId ?? ""),
    enabled: !!company,
  });
  const { data: wages } = useQuery({
    queryKey: ["personnelWages"],
    queryFn: getAllWageTypes,
  });
  const { data: roles } = useQuery({
    queryKey: ["personnelRoles"],
    queryFn: getAllRoles,
  });

  const onModalPerformsSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: ["allLocationUsers", company?.companyId],
    });
    queryClient.invalidateQueries({ queryKey: ["personnelWages"] });
    queryClient.invalidateQueries({ queryKey: ["personnelRoles"] });
  };

  const activeColumns: DataTableColumn<Personnel>[] = useMemo(
    () => [
      {
        header: t("common.Personnel"),
        accessorKey: "fullName",
        cell: ({ row }) => (
          <Info
            name={row.original.fullName}
            userName={row.original.userName}
            userPhoto={row.original.userPhoto}
          />
        ),
        enableSorting: true,
        size: 300,
      },
      {
        header: t("personnel.Connections"),
        accessorKey: "locationName",
        cell: ({ row }) => (
          <div className="flex flex-row flex-wrap  gap-3">
            {row.original.locationName.split(",").map((l, index) => (
              <Badge
                // type={row.original?.userRole === "Manager" ? "info" : "success"}
                type="info"
                key={row.original?.userId + index}
              >
                {/* {l + ": " + row.original?.userRole} */}
                {l}
              </Badge>
            ))}
          </div>
        ),
      },
      {
        header: t("common.Status"),
        accessorKey: "isDeleted",
        size: 94,
        cell: ({ row }) =>
          row.original.isDeleted ? (
            <Badge type="danger">{t("common.Inactive")}</Badge>
          ) : (
            <Badge type="success">{t("common.Active")}</Badge>
          ),
      },
      {
        align: "center",
        header: t("common.Actions"),
        size: 110,
        cell: ({ row }) => (
          <ActiveActions
            personnel={row.original}
            setDeactivateModal={setDeactivatePersonnelModalShow}
            setIsOpenEditPersonnelModal={setIsOpenEditPersonnelModal}
            setEditPasswordModal={setIsOpenPasswordEditModal}
          />
        ),
      },
    ],
    [t],
  );

  const inactiveColumns: DataTableColumn<Personnel>[] = useMemo(
    () => [
      {
        header: t("common.Personnel"),
        accessorKey: "fullName",
        cell: ({ row }) => (
          <Info
            name={row.original.fullName}
            userName={row.original.userName}
            userPhoto={row.original.userPhoto}
          />
        ),
        enableSorting: true,
      },
      {
        header: t("common.Status"),
        accessorKey: "isDeleted",
        size: 94,
        cell: ({ row }) =>
          row.original.isDeleted ? (
            <Badge type="danger">{t("common.Inactive")}</Badge>
          ) : (
            <Badge type="success">{t("common.Active")}</Badge>
          ),
      },
      {
        header: t("common.Actions"),
        size: 110,
        cell: ({ row }) => (
          <InactiveActions
            setIsOpenEditPersonnelModal={setIsOpenEditPersonnelModal}
            setActivateModal={setActivatePersonnelModalShow}
            setEditPasswordModal={setIsOpenPasswordEditModal}
            personnel={row.original}
          />
        ),
      },
    ],
    [t],
  );

  return (
    <div className="flex flex-col gap-7.5">
      <DataTable
        loading={isPending}
        onRowClick={setSelectedPersonnel}
        columns={activeColumns}
        data={users?.alllocationusers
          ?.filter((m) => !m.isDeleted)
          .sort((a, b) => (a.fullName.localeCompare(b.fullName) > 0 ? 1 : -1))}
        title={t("personnel.Active Personnel")}
        searchPlaceholder={t("common.Search Personnel")}
        enableSearch
        enablePagination
        header={
          <Button onClick={() => setIsOpenAddPersonnelModal(true)}>
            <i className="ki-filled ki-plus-squared"></i>
            {t("navigation.Add New Personnel")}
          </Button>
        }
      />
      <DataTable
        columns={inactiveColumns}
        onRowClick={setSelectedPersonnel}
        loading={isPending}
        enableSearch
        enablePagination
        data={users?.alllocationusers
          ?.filter((m) => m.isDeleted)
          .sort((a, b) => (a.fullName.localeCompare(b.fullName) > 0 ? 1 : -1))}
        title={t("personnel.Inactive Personnel")}
        searchPlaceholder={t("common.Search Personnel")}
      />
      <EditPasswordModal
        open={isOpenPasswordEditModal}
        onClose={() => setIsOpenPasswordEditModal(false)}
        personnel={selectedPersonnel}
      />
      <EditPersonnelModal
        open={isOpenEditPersonnelModal}
        onClose={() => setIsOpenEditPersonnelModal(false)}
        roles={roles}
        wages={wages}
        locations={users?.alllocationlist}
        personnel={selectedPersonnel}
        companyId={company?.companyId}
        onSuccess={onModalPerformsSuccess}
      />
      <AddPersonnelModal
        open={isOpenAddPersonnelModal}
        onClose={() => setIsOpenAddPersonnelModal(false)}
        companyId={company?.companyId ?? ""}
        roles={roles}
        wages={wages}
        locations={users?.alllocationlist}
        onSuccess={onModalPerformsSuccess}
      />
      <DeactivatePersonnelModal
        companyId={company?.companyId ?? ""}
        open={deactivatePersonnelModalShow}
        onClose={() => setDeactivatePersonnelModalShow(false)}
        userId={selectedPersonnel?.userId ?? ""}
        personnelName={selectedPersonnel?.fullName ?? ""}
      />
      <ActivatePersonnelModal
        userId={selectedPersonnel?.userId ?? ""}
        open={activatePersonnelModalShow}
        onClose={() => setActivatePersonnelModalShow(false)}
        companyId={company?.companyId ?? ""}
        personnelName={selectedPersonnel?.fullName ?? ""}
      />
    </div>
  );
}
